import { useState, useEffect } from "react";
import { dateLocale } from "../lib/languages";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowLeft, ListChecks, Plus, Trash2, Save, Check, History, Settings, ChevronDown, ChevronRight, GripVertical
} from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";
import { useSharePrompt } from "../hooks/useSharePrompt";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useI18n } from "../lib/i18n";

const MorningChecklistPage = ({ user, logout }) => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayEntry, setTodayEntry] = useState(null);

  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templateRes, todayRes, historyRes] = await Promise.all([
        axios.get(`${API}/morning-checklist/template`),
        axios.get(`${API}/morning-checklist/today`),
        axios.get(`${API}/morning-checklist?limit=14`),
      ]);

      setItems(templateRes.data.items || []);
      setHistory(historyRes.data || []);

      if (todayRes.data) {
        setTodayEntry(todayRes.data);
        setChecked(todayRes.data.checked || {});
        setNotes(todayRes.data.notes || "");
      }
    } catch (err) {
      console.error("Error loading checklist:", err);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    const updated = [...items, newItem.trim()];
    setItems(updated);
    setNewItem("");
    saveTemplate(updated);
  };

  const removeItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    const newChecked = { ...checked };
    delete newChecked[index.toString()];
    setChecked(newChecked);
    saveTemplate(updated);
  };

  const saveTemplate = async (itemsList) => {
    try {
      await axios.put(`${API}/morning-checklist/template`, { items: itemsList });
    } catch (err) {
      console.error("Error saving template:", err);
    }
  };

  const toggleCheck = (index) => {
    setChecked(prev => ({
      ...prev,
      [index.toString()]: !prev[index.toString()],
    }));
  };

  const completedCount = Object.values(checked).filter(Boolean).length;
  const progress = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  const saveChecklist = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/morning-checklist`, {
        items,
        checked,
        notes,
        date: new Date().toISOString().split("T")[0],
      });
      setTodayEntry(res.data);
      toast.success(t("checklist.saved"));
      promptShare(res.data, "morning_checklist");

      // Refresh history
      const historyRes = await axios.get(`${API}/morning-checklist?limit=14`);
      setHistory(historyRes.data || []);
    } catch (err) {
      toast.error(t("checklist.errorSaving"));
      console.error("Error saving checklist:", err);
    } finally {
      setSaving(false);
    }
  };

  const getCompletionEmoji = (pct) => {
    if (pct >= 100) return t("checklist.perfect");
    if (pct >= 75) return t("checklist.great");
    if (pct >= 50) return t("checklist.keepGoing");
    return t("checklist.courage");
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse h-12 bg-muted rounded-lg" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="morning-checklist-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold flex items-center gap-2">
              <ListChecks className="w-7 h-7 text-emerald-500" />
              {t("checklist.title")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {new Date().toLocaleDateString(dateLocale(locale), { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{completedCount} / {items.length} {t("checklist.tasks")}</span>
              <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                {getCompletionEmoji(progress)}
              </span>
            </div>
            <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
                data-testid="progress-bar"
              />
            </div>
          </CardContent>
        </Card>

        {/* Checklist items */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("checklist.myTasks")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                    checked[index.toString()]
                      ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => toggleCheck(index)}
                  data-testid={`checklist-item-${index}`}
                >
                  <div
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      checked[index.toString()]
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {checked[index.toString()] && <Check className="w-4 h-4" />}
                  </div>
                  <span
                    className={`flex-1 text-sm ${
                      checked[index.toString()] ? "line-through text-muted-foreground" : ""
                    }`}
                  >
                    {item}
                  </span>
                  {showSettings && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Add new item */}
            {showSettings && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder={t("checklist.addTask")}
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && addItem()}
                  data-testid="new-item-input"
                />
                <Button size="icon" onClick={addItem} disabled={!newItem.trim()} data-testid="add-item-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("checklist.dailyNotes")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("checklist.notesPlaceholder")}
              className="min-h-[80px] resize-none"
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={saveChecklist}
            disabled={saving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="save-checklist-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t("common.saving") : t("common.save")}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="history-btn"
          >
            <History className="w-4 h-4" />
          </Button>
        </div>

        {/* History */}
        {showHistory && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                {t("checklist.history")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-2">
                  {history.map((entry, i) => {
                    const entryItems = entry.items || [];
                    const entryChecked = entry.checked || {};
                    const done = Object.values(entryChecked).filter(Boolean).length;
                    const total = entryItems.length;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`history-entry-${i}`}>
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {new Date(entry.date || entry.created_at).toLocaleDateString(dateLocale(locale), { weekday: "short", day: "numeric", month: "short" })}
                          </p>
                          {entry.notes && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{entry.notes}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{done}/{total}</span>
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-400"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">{t("checklist.noHistory")}</p>
              )}
            </CardContent>
          </Card>
        )}

        <ShareExerciseDialog
          open={!!shareData}
          entryId={shareData?.entryId}
          exerciseType={shareData?.exerciseType}
          onClose={closeSharePrompt}
        />
      </div>
    </DashboardLayout>
  );
};

export default MorningChecklistPage;
